import notes from '@/sui/data/sheets/edit/notes'
import bounds from '@/sui/data/sheets/edit/bounds'
import assert from '@/sui/tools/assert'

export function checkAge (result, sheet, fields) {
  assert.notNull(fields.unitType)
  assert.notNull(fields.isAdult)
  assert.notNull(fields.childAge)

  const type = sheet[fields.unitType]
  const isAdult = sheet[fields.isAdult]
  if (type == null || isAdult == null) {
    return
  }

  const msg = `Incohérence possible entre l'âge du patient et l'unité dans
laquelle il se trouve`
  const neo = type.match(/^NEO/) != null
  const ped = type.match(/^(MIX|PED)/) != null
  const child = neo || ped
  if ((isAdult === 1 && child) ||
      (isAdult === 2 && !child)) {
    const note = result[fields.isAdult]
    note.type = 'warning'
    note.tooltip = msg
  }

  const childAge = sheet[fields.childAge]
  if ((neo && (childAge === 2 || childAge === 3)) ||
      (ped && (childAge === 1 || childAge === 4))) {
    const note = result[fields.childAge]
    note.type = 'warning'
    note.tooltip = msg
  }
}

export function checkDate (
  result, sheet, year, ofields
) {
  assert.notNull(ofields.obsDate)
  const oldest = {
    date: new Date(year, 0, 1),
    text: `date de début de l'année (01/01/${year})`
  }

  const newest = {
    date: new Date(year, 11, 31),
    text: `date de fin de l'année (31/12/${year})`
  }

  const obsDate = notes.dateField(result, sheet, ofields.obsDate)
  const c = [
    { f: oldest, s: obsDate },
    { f: obsDate, s: newest }
  ]

  if (year === 2023) {
    assert.notNull(ofields.insertionDate)
    assert.notNull(ofields.lastDressingDate)
    c.push(
      {
        f: notes.dateField(result, sheet, ofields.insertionDate),
        s: obsDate
      },
      {
        f: notes.dateField(result, sheet, ofields.lastDressingDate),
        s: obsDate
      }
    )
  }
  notes.compareDates(c)
}

export function checkCompresses (result, sheet, fields, prefix = 'p1p2p3p4') {
  assert.notNull(fields.antiseptic)
  assert.notNull(fields.useCompresses)

  const antiseptic = sheet[fields.antiseptic]
  const useCompresses = sheet[fields.useCompresses]
  if (useCompresses === 3 && antiseptic !== 7) {
    const anote = result[fields.antiseptic]
    const ucnote = result[fields.useCompresses]
    anote.type = ucnote.type = 'error'
    anote.tooltip = ucnote.tooltip = `La valeur «Sans objet» pour les
compresses n'est valide que pour les antiseptiques avec applicateur`
  }
}

export function checkGlovesPutOn (result, sheet, fields) {
  assert.notNull(fields.gloves)
  assert.notNull(fields.glovesPutOn)
  assert.notNull(fields.handHygieneBeforePeriInsertion)
  assert.notNull(fields.whyNotHH)

  const gl = fields.gloves
  const gpo = fields.glovesPutOn
  const shh = fields.handHygieneBeforePeriInsertion
  const wnh = fields.whyNotHH

  const whyNotHH = sheet[wnh]
  const gloves = sheet[gl]
  const glovesPutOn = sheet[gpo]
  const secondaryHH = sheet[shh]
  if (secondaryHH === 1 && (glovesPutOn === 1 || glovesPutOn === 2)) {
    const ngpo = result[gpo]
    const nshh = result[shh]
    ngpo.type = nshh.type = 'error'
    ngpo.tooltip = nshh.tooltip = `Les gants ayant déjà été enfilés,
il ne peut y avoir de seconde HDM juste avant l'insertion du cathéter`
  }

  if (whyNotHH === 2) {
    const nwnh = result[wnh]
    if (gloves != null && gloves !== 1) {
      const ngl = result[gl]
      nwnh.type = ngl.type = 'error'
      nwnh.tooltip = ngl.tooltip = `Incohérence entre les champs
«Port des gants» et «Pourquoi l'HDM n'a-t-elle pas été réalisée avant
l'insertion du cathéter ?»`
    } else if (glovesPutOn != null && glovesPutOn !== 1 && glovesPutOn !== 2) {
      const ngpo = result[gpo]
      nwnh.type = ngpo.type = 'error'
      nwnh.tooltip = ngpo.tooltip = `Incohérence entre les champs
«Gants enfilés» et «Pourquoi l'HDM n'a-t-elle pas été réalisée avant
l'insertion du cathéter ?»`
    }
  }
}

export function checkB2Gloves (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.whyNotHHM)
  assert.notNull(fields.gloves)
  assert(fields.glovesPutOnManip !== null) // undefined is ok
  assert(fields.handHygieneBeforeB2Manip !== null) // undefined is ok

  if (sheet[fields.obsManagment] !== 5) {
    return
  }

  const notHHM = sheet[fields.whyNotHHM]
  if (notHHM === 6) {
    const gloves = sheet[fields.gloves]
    if (gloves === 2) {
      const nnhhm = result[fields.whyNotHHM]
      const ngl = result[fields.gloves]
      ngl.type = nnhhm.type = 'error'
      ngl.tooltip = nnhhm.tooltip = `Incohérence entre les champs
«Port des gants» et «Pourquoi l'HDM n'a-t-elle pas réalisée immédiatement
avant la manipulation du site de branchement ?»`
    }
  }

  if (fields.glovesPutOnManip != null || fields.handHygieneBeforeB2Manip != null) {
    const glovesPutOn = sheet[fields.glovesPutOnManip]
    const hhm = sheet[fields.handHygieneBeforeB2Manip]

    if (glovesPutOn === 1 && hhm === 1) {
      const ngpo = result[fields.glovesPutOnManip]
      const nhhm = result[fields.handHygieneBeforeB2Manip]
      nhhm.type = ngpo.type = 'error'
      nhhm.tooltip = ngpo.tooltip = `Impossible d'effectuer l'hygiène des mains
avec des gants.`
    }
  }
}

export function checkDeviceInsertionSite (result, sheet, fid, fields, sh) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.catheterType)
  assert.notNull(fields.insertionSite)

  const obs = sheet[fields.obsManagment]
  if (obs !== fid) {
    return
  }

  const device = sheet[fields.catheterType]
  const insertionSite = sheet[fields.insertionSite]

  if (device == null || insertionSite == null) {
    return
  }

  const controls = [
    {
      device: 3,
      sites: [7, 10],
      tooltip: `les PICCs sont généralement insérés dans un membre supérieur`
    },
    {
      device: 2,
      sites: [10, 12],
      tooltip: `les cathéters artériels sont généralement insérés en radial`
    },
    {
      device: 4,
      sites: [7, 10],
      tooltip: `les MID line avec prolongateur sont généralement insérés dans un membre supérieur`
    }
  ]

  for (let i = 0; i !== controls.length; ++i) {
    const c = controls[i]
    if (device === c.device && c.sites.indexOf(insertionSite) === -1) {
      const deviceNote = result[fields.catheterType]
      const insertionSiteNote = result[fields.insertionSite]

      deviceNote.type = insertionSiteNote.type = 'warning'
      deviceNote.tooltip = insertionSiteNote.tooltip =
        `Incohérence possible : ${c.tooltip}`
      break
    }
  }
}

export function checkR1 (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.whyNotSGForAntisepsis)
  assert.notNull(fields.dressingSet)
  assert.notNull(fields.changeStabilizer)
  assert.notNull(fields.newStabilizer)
  assert.notNull(fields.hasStabilizer)

  if (sheet[fields.obsManagment] !== 7) {
    return
  }

  const wnfa = sheet[fields.whyNotSGForAntisepsis]
  const ds = sheet[fields.dressingSet]
  if (wnfa === 2 && ds === 2) {
    const nwnfa = result[fields.whyNotSGForAntisepsis]
    const nds = result[fields.dressingSet]
    nwnfa.type = nds.type = 'error'
    nwnfa.tooltip = nds.tooltip = `Incohérence entre les champs
«Utilisation d'un set à pansement» et «Pourquoi les gants ne sont pas
stériles pour la réfection du pansement ?».`
  }

  const cs = sheet[fields.changeStabilizer]
  const ns = sheet[fields.newStabilizer]
  if (cs === 1 && ns === 2) {
    const ncs = result[fields.changeStabilizer]
    const nns = result[fields.newStabilizer]
    ncs.type = nns.type = 'warning'
    ncs.tooltip = nns.tooltip = `Incohérence possible : le stabilisateur retiré n'a pas
été remplacé.`
  }

  const hs = sheet[fields.hasStabilizer]
  if (hs === 2 && ns === 1) {
    const nhs = result[fields.hasStabilizer]
    const nns = result[fields.newStabilizer]
    nhs.type = nns.type = 'warning'
    nhs.tooltip = nns.tooltip = `Incohérence possible : un nouveau stabilisateur a été
mis en place alors qu'il n'y en avait pas auparavant.`
  }
}

export function checkProbeTouchedWithGloves (
  result, sheet, obsManagment, fields, reference
) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.probeTouchedWith)

  const obs = sheet[fields.obsManagment]
  if (obsManagment !== obs) {
    return
  }

  const ptwf = fields.probeTouchedWith
  const ptw = sheet[ptwf]
  if (ptw !== 1) {
    return
  }

  if (reference.ignore(sheet, sheet)) {
    const nptw = result[ptwf]
    nptw.type = 'error'
    nptw.tooltip = `L'opérateur ne porte pas de gants au moment
où l'échographe est touché.`
  }
}

export function checkBMI (result, sheet, fields) {
  assert.notNull(fields.obsManagment)

  if (sheet[fields.obsManagment] !== 7) {
    return
  }

  notes.checkNumerics(result, sheet, [bounds.bmi])
}

export function checkP1Chlorex (result, sheet, fields) {
  assert.notNull(fields.catheterType)
  assert.notNull(fields.whyNotChlAlc2Percent)
  assert.notNull(fields.obsManagment)

  const ctf = fields.catheterType
  const ncaf = fields.whyNotChlAlc2Percent

  const ct = sheet[ctf]
  if (sheet[fields.obsManagment] !== 1 ||
      ct == null || ct === 4 || sheet[ncaf] !== 5) {
    return
  }

  const nca = result[ncaf]
  const cta = result[ctf]
  nca.type = cta.type = 'error'
  nca.tooltip = cta.tooltip = `Incohérence entre le dispositif
et la raison de non utilisation de la chlorexidine alcoolique à 2%.`
}

export function checkSecondManipType (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.manipType)
  assert.notNull(fields.secondManipType)
  assert.notNull(fields.otherManipType)
  assert.notNull(fields.secondOtherManipType)

  const obs = sheet[fields.obsManagment]
  if (obs !== 5) {
    return
  }

  const mt = sheet[fields.manipType]
  const smt = sheet[fields.secondManipType]
  if (mt == null || smt !== mt) {
    return
  }

  if (mt !== 6) {
    const nmt = result[fields.manipType]
    const nsmt = result[fields.secondManipType]
    nmt.type = nsmt.type = 'error'
    nmt.tooltip = nsmt.tooltip = `Les deux types de manipulation
doivent être différents.`
    return
  }

  const mtd = sheet[fields.otherManipType]
  const smtd = sheet[fields.secondOtherManipType]
  if (mtd === smtd) {
    const nmtd = result[fields.otherManipType]
    const nsmtd = result[fields.secondOtherManipType]
    nmtd.type = nsmtd.type = 'error'
    nmtd.tooltip = nsmtd.tooltip = `Les deux types de manipulation
doivent être différents.`
  }
}

export function checkHeadFaceSterileField (result, sheet, fields) {
  assert.notNull(fields.patientHeadSterileField)
  assert.notNull(fields.patientSterileField)

  const headSF = sheet[fields.patientHeadSterileField]
  const faceSF = sheet[fields.patientSterileField]

  if (headSF == null || faceSF == null || headSF === faceSF) {
    return
  }

  const h = result[fields.patientHeadSterileField]
  const f = result[fields.patientSterileField]

  const t1 = headSF === 1 ? 'la tête est couverte' : 'le visage est couvert'
  const t2 = headSF === 1 ? 'le visage' : 'la tête'

  h.type = f.type = 'warning'
  h.tooltip = f.tooltip = `Incohérence possible :
si ${t1} par un champ stérile, ${t2} doit probablement l'être également.`
}

export function checkEhpadService (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.isEhpad)
  assert.notNull(fields.unitType)

  if (sheet[fields.obsManagment] !== 6) {
    return
  }

  const isEhpad = sheet[fields.isEhpad]
  if (isEhpad == null) {
    return
  }

  const unitType = sheet[fields.unitType]
  if (isEhpad === 1 && unitType !== 'AD EHP') {
    const ehpadNote = result[fields.isEhpad]
    const unitTypeNote = result[fields.unitType]
    ehpadNote.type = unitTypeNote.type = 'error'
    ehpadNote.tooltip = unitTypeNote.tooltip = `Incohérence :
Le code service doit être «AD EHP» si l'établissement est un EHPAD.`
  }
}

export function checkIsolatedRinsingOfContinuousCatheter (
  result, sheet, fields
) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.isolatedRinsingReason)
  assert.notNull(fields.deviceUsage)

  if (![4, 5].includes(sheet.obsManagment)) {
    return
  }

  const isolatedRinsingReason = sheet[fields.isolatedRinsingReason]
  const deviceUsageField = sheet[fields.deviceUsage]
  if (isolatedRinsingReason == null || deviceUsageField == null) {
    return
  }

  if (isolatedRinsingReason === 2 && deviceUsageField === 1) {
    const deviceUsageNote = result[fields.deviceUsage]
    const isolatedRinsingNote = result[fields.isolatedRinsingReason]
    deviceUsageNote.type = isolatedRinsingNote.type = 'warning'
    deviceUsageNote.tooltip = isolatedRinsingNote.tooltip = `Incohérence possible :
un cathéter en utilisation continue a probablement été manipulé au cours des 7
derniers jours.`
  }
}

export function checkGelAppliedWithoutSheath (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.gelApplied)
  assert.notNull(fields.insertionUltrasoundSheath)

  const obs = sheet[fields.obsManagment]
  if (![1, 2].includes(obs)) {
    return
  }

  const gelApplied = sheet[fields.gelApplied]
  const ultrasoundSheath = sheet[fields.insertionUltrasoundSheath]

  if (gelApplied == null || ultrasoundSheath == null) {
    return
  }

  if ((gelApplied === 3 || gelApplied === 1) && ultrasoundSheath === 3) {
    const gelAppliedNote = result[fields.gelApplied]
    gelAppliedNote.type = 'error'
    gelAppliedNote.tooltip = `La gaine/housse n'est pas utilisée,
    le gel ne peut pas être appliqué sur celle-ci`
  }
}

export function checkVialOpenedSince (result, sheet, fields) {
  assert.notNull(fields.vialOpeningDateWritten)
  assert.notNull(fields.vialOpenedSince)

  const vialOpeningDateWritten = sheet[fields.vialOpeningDateWritten]
  const vialOpenedSince = sheet[fields.vialOpenedSince]

  if (vialOpeningDateWritten === 1 && vialOpenedSince === 3) {
    const vodwNotes = result[fields.vialOpeningDateWritten]
    const vosNotes = result[fields.vialOpenedSince]

    vodwNotes.type = vosNotes.type = 'warning'
    vodwNotes.tooltip = vosNotes.tooltip = `Incohérence : si la date d'ouverture est connue,
    il devrait être possible de savoir si il est ouvert depuis plus de 24h`
  }
}

export function checkGelUsed (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.searchGelUsed)
  assert.notNull(fields.insertionGelUsed)

  const obs = sheet[fields.obsManagment]
  if (![1, 2].includes(obs)) {
    return
  }

  const s = fields.searchGelUsed
  const i = fields.insertionGelUsed
  const sgu = sheet[s]
  const igu = sheet[i]

  if (sgu == null || igu == null || sgu !== 1 || igu !== 2) {
    return
  }

  const sguN = result[s]
  const iguN = result[i]
  sguN.type = iguN.type = 'warning'
  sguN.tooltip = iguN.tooltip =
    `Incohérence possible : le gel utilisé pour le repérage est stérile
mais le gel utilisé lors de la pose ne l'est pas.`
}

export function checkRinsingSuccessivePushesCount (
  result, sheet, fields
) {
  assert.notNull(fields.obsManagment)

  if (sheet[fields.obsManagment] === 7) {
    return
  }

  notes.checkNumerics(
    result, sheet, [bounds.rinsingSuccessivePushesCount]
  )
}

export function checkNeedleWithdrawalManipType (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.manipType)
  assert.notNull(fields.catheterType)

  const obs = sheet[fields.obsManagment]
  if (obs !== 5) {
    return
  }

  const mtField = fields.manipType
  const ctField = fields.catheterType

  const mt = sheet[mtField]
  const ct = sheet[ctField]
  if (mt == null || mt !== 7 || ct == null || ct === 10) {
    return
  }

  const nmt = result[mtField]
  const nct = result[ctField]
  nmt.type = nct.type = 'error'
  nmt.tooltip = nct.tooltip = `Incohérence entre le type de cathéter
et le type de manipulation : le retrait d'aiguille se fait uniquement sur CCI.`
}

export function checkP5IsAdult (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.unitType)

  const obs = sheet[fields.obsManagment]
  if (obs !== 8) {
    return
  }

  const type = sheet[fields.unitType]
  if (type == null) {
    return
  }

  const isChild = type.match(/^(NEO|MIX|PED)/) != null
  if (isChild) {
    const note = result[fields.unitType]
    note.type = 'error'
    note.tooltip = `La fiche P5 est réservée aux patients adultes.`
  }
}

export function checkInjectedRinsingVolume (result, sheet, fields) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.injectedRinsingVolume)
  assert.notNull(fields.syringeCapacity)
  assert.notNull(fields.syringeCapacityOther)

  const obs = sheet[fields.obsManagment]
  if (obs !== 4 && obs !== 5) {
    return
  }

  const injectedVolume = sheet[fields.injectedRinsingVolume]
  if (injectedVolume == null || injectedVolume === 2 || injectedVolume === 9) {
    return
  }

  const syringeCapacity = sheet[fields.syringeCapacity]
  if (syringeCapacity == null) {
    return
  }

  let syringeVolume = sheet[fields.syringeCapacityOther]
  if (syringeCapacity !== 3) {
    syringeVolume = syringeCapacity === 1 ? 10 : 20
  }

  const capNote = result[fields.syringeCapacity]
  const capOtherNote = result[fields.syringeCapacityOther]
  const injNote = result[fields.injectedRinsingVolume]
  if (injectedVolume === 1 && syringeVolume < 10) {
    injNote.type = capOtherNote.type = 'error'
    injNote.tooltip = capOtherNote.tooltip =
      `Incohérence : le volume injecté est supérieur à la capacité de la seringue.`
    return
  }

  if (injectedVolume === 3 && syringeVolume <= 10) {
    let targetNote = syringeCapacity !== 3 ? capNote : capOtherNote
    injNote.type = targetNote.type = 'error'
    injNote.tooltip = targetNote.tooltip =
      `Incohérence : le volume injecté est supérieur à la capacité de la seringue.`
  }
}

export function checkAntiseptic (
  result, sheet, sheetValue, fields
) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.antiseptic)
  assert.notNull(fields.valveAntiseptic)

  const obs = sheet[fields.obsManagment]
  if (obs !== sheetValue) {
    return
  }

  const antiseptic = sheet[fields.antiseptic]
  if (antiseptic == null) {
    return
  }

  const valveAntiseptic = sheet[fields.valveAntiseptic]
  if (valveAntiseptic == null || valveAntiseptic === antiseptic) {
    return
  }

  const antisepticNote = result[fields.antiseptic]
  const valveAntisepticNote = result[fields.valveAntiseptic]
  antisepticNote.type = valveAntisepticNote.type = 'warning'
  antisepticNote.tooltip = valveAntisepticNote.tooltip =
    `Incohérence possible : l'antiseptique utilisé pour la valve est différent de celui utilisé pour la connectique.`
}

export function checkFreeFlowWithDiscontinuousUsage (
  result, sheet, fields
) {
  assert.notNull(fields.obsManagment)
  assert.notNull(fields.whyNotRinsingDoneWhenManipulating)
  assert.notNull(fields.deviceUsage)

  const obs = sheet[fields.obsManagment]
  if (obs !== 4 && obs !== 5) {
    return
  }

  const whyField = fields.whyNotRinsingDoneWhenManipulating
  const duField = fields.deviceUsage

  const why = sheet[whyField]
  const du = sheet[duField]

  if (why == null || why !== 6 || du == null || du !== 2) {
    return
  }

  const nwhy = result[whyField]
  const ndu = result[duField]
  nwhy.type = ndu.type = 'warning'
  nwhy.tooltip = ndu.tooltip = `Incohérence possible : en utilisation discontinue, la
confusion avec le débit libre semble peu probable.`
}
